import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, ExternalLink, Link2, Lock, Plus, RotateCcw, Search, X } from 'lucide-react';
import PageSkeleton from '../common/Skeleton';
import { Button, AlertMessage, PageHeader, Modal, Textarea, ConfirmModal } from '../common/ui';
import InternalNotesThread, { InternalNotesThreadHandle } from './InternalNotesThread';
import VascoDocumentRow, { VascoChip } from './VascoDocumentRow';
import { formatDateTime } from '../../utils/dateUtils';
import { getWooCommerceOrderUrl } from './types';
import type {
  B2BOrderDetailData,
  OrderCoverage,
  OrderLinkedPayment,
  OrderPaymentCandidate,
  OrderVascoDocument,
} from './types';

// Vasco document numbers are "RACUN/LETO", e.g. "3814/2026".
const VASCO_NUMBER_RE = /^\d+\/\d{4}$/;

const COVERAGE_LABELS: Record<OrderCoverage['status'], string> = {
  overpaid: 'Overpaid',
  full: 'Paid',
  partial: 'Partially paid',
  none: 'Not paid',
};

// Overpaid is orange: two or more payments together pay more than the order
// total, so there may be a double charge or a wrong link to check.
const COVERAGE_CLASSES: Record<OrderCoverage['status'], string> = {
  overpaid: 'text-orange-700 bg-orange-50',
  full: 'text-green-700 bg-green-50',
  partial: 'text-blue-700 bg-blue-50',
  none: 'text-red-600 bg-red-50',
};

const B2BOrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [data, setData] = useState<B2BOrderDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // A true 404 means the order does not exist; anything else is a transient
  // failure the user can retry, so the two empty states differ.
  const [notFound, setNotFound] = useState(false);
  const [loadErrorStatus, setLoadErrorStatus] = useState<number | null>(null);

  // Payment picker
  const [paymentSearch, setPaymentSearch] = useState('');
  const [candidates, setCandidates] = useState<OrderPaymentCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [amountInput, setAmountInput] = useState('');
  const [linkingPaymentId, setLinkingPaymentId] = useState<number | null>(null);
  const [removingPaymentId, setRemovingPaymentId] = useState<number | null>(null);

  // Vasco linking
  const [vascoNumberInput, setVascoNumberInput] = useState('');
  const [vascoLinking, setVascoLinking] = useState(false);
  const [vascoError, setVascoError] = useState('');
  const [removingVascoLinkId, setRemovingVascoLinkId] = useState<number | null>(null);

  // Force close / reopen (migration 0120). The server adds a line to the
  // internal notes, so the thread is refreshed after each action.
  const notesRef = useRef<InternalNotesThreadHandle>(null);
  const [showForceCloseModal, setShowForceCloseModal] = useState(false);
  const [forceCloseNote, setForceCloseNote] = useState('');
  const [forceClosing, setForceClosing] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const { data: detail } = await axios.get<B2BOrderDetailData>(`/api/accounting/b2b-orders/${id}`);
      setData(detail);
      setNotFound(false);
      setLoadErrorStatus(null);
    } catch (err: any) {
      const status = err?.response?.status ?? null;
      setLoadErrorStatus(status);
      setNotFound(status === 404);
      setError(err?.response?.data?.error || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  // Debounced payment search. Two characters is the shortest term that is worth
  // a round trip.
  useEffect(() => {
    const term = paymentSearch.trim();
    if (term.length < 2) {
      setCandidates([]);
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data: result } = await axios.get<{ payments: OrderPaymentCandidate[] }>(
          `/api/accounting/b2b-orders/${id}/payment-search`,
          { params: { search: term }, signal: controller.signal }
        );
        setCandidates(result.payments);
      } catch (err: any) {
        if (axios.isCancel(err) || err.name === 'CanceledError') return;
        setCandidates([]);
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [paymentSearch, id]);

  const applyPaymentsPayload = (payload: { payments: OrderLinkedPayment[]; coverage: OrderCoverage }) => {
    setData((prev) => (prev ? { ...prev, payments: payload.payments, coverage: payload.coverage } : prev));
  };

  const handleLinkPayment = async (paymentId: number) => {
    setLinkingPaymentId(paymentId);
    setError('');
    try {
      const body: { paymentId: number; partialAmount?: string } = { paymentId };
      if (amountInput.trim()) body.partialAmount = amountInput.trim();
      const { data: result } = await axios.post(`/api/accounting/b2b-orders/${id}/link-payment`, body);
      applyPaymentsPayload(result);
      setPaymentSearch('');
      setCandidates([]);
      setAmountInput('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to link payment');
    } finally {
      setLinkingPaymentId(null);
    }
  };

  const handleRemovePayment = async (paymentId: number) => {
    setRemovingPaymentId(paymentId);
    setError('');
    try {
      const { data: result } = await axios.delete(`/api/accounting/b2b-orders/${id}/payments/${paymentId}`);
      applyPaymentsPayload(result);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove payment');
    } finally {
      setRemovingPaymentId(null);
    }
  };

  const handleLinkVasco = async () => {
    const invoiceNumber = vascoNumberInput.trim();
    if (!VASCO_NUMBER_RE.test(invoiceNumber)) {
      setVascoError('Enter a Vasco document number like 3814/2026');
      return;
    }
    setVascoLinking(true);
    setVascoError('');
    try {
      const { data: result } = await axios.post(`/api/accounting/b2b-orders/${id}/link-vasco-invoice`, {
        invoiceNumber,
      });
      setData((prev) => (prev ? { ...prev, vascoDocuments: result.vascoDocuments } : prev));
      setVascoNumberInput('');
    } catch (err: any) {
      setVascoError(err.response?.data?.error || 'Failed to link Vasco invoice');
    } finally {
      setVascoLinking(false);
    }
  };

  const handleForceClose = async () => {
    if (!forceCloseNote.trim() || forceClosing) return;
    setForceClosing(true);
    try {
      const { data: result } = await axios.post(`/api/accounting/b2b-orders/${id}/force-close`, {
        note: forceCloseNote.trim(),
      });
      setData((prev) => (prev ? { ...prev, order: result.order } : prev));
      setShowForceCloseModal(false);
      setForceCloseNote('');
      notesRef.current?.refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to force close order');
    } finally {
      setForceClosing(false);
    }
  };

  const handleReopen = async () => {
    try {
      const { data: result } = await axios.post(`/api/accounting/b2b-orders/${id}/reopen`, {});
      setData((prev) => (prev ? { ...prev, order: result.order } : prev));
      notesRef.current?.refresh();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reopen order');
    } finally {
      setShowReopenModal(false);
    }
  };

  const handleRemoveVascoLink = async (linkId: number) => {
    setRemovingVascoLinkId(linkId);
    setVascoError('');
    try {
      const { data: result } = await axios.delete(`/api/accounting/b2b-orders/${id}/vasco-links/${linkId}`);
      setData((prev) => (prev ? { ...prev, vascoDocuments: result.vascoDocuments } : prev));
    } catch (err: any) {
      setVascoError(err.response?.data?.error || 'Failed to remove Vasco link');
    } finally {
      setRemovingVascoLinkId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Order Detail</h1>
        <PageSkeleton rows={3} rowHeight="h-24" />
      </div>
    );
  }

  if (!data) {
    if (notFound) {
      return (
        <div className="p-6">
          <div className="text-center py-12">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Order Not Found</h3>
            <Button variant="secondary" onClick={() => navigate('/accounting/b2b-orders')} icon={undefined}>
              Back to B2B Orders
            </Button>
          </div>
        </div>
      );
    }
    const statusLabel = loadErrorStatus ? ` (HTTP ${loadErrorStatus})` : '';
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load order{statusLabel}</h3>
          <p className="text-sm text-gray-600 mb-4">
            {error || 'The server returned an error while loading this order.'}
          </p>
          <div className="flex items-center justify-center gap-2">
            <Button variant="primary" onClick={() => fetchOrder()} icon={undefined}>
              Retry
            </Button>
            <Button variant="secondary" onClick={() => navigate('/accounting/b2b-orders')} icon={undefined}>
              Back to B2B Orders
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { order, payments, coverage, vascoDocuments, creditNotes } = data;
  const orderTotal = parseFloat(order.total) || 0;
  const customer =
    order.billingCompany?.trim()
    || [order.billingFirstName, order.billingLastName].filter(Boolean).join(' ').trim()
    || order.customerName
    || '—';
  const personalName = [order.billingFirstName, order.billingLastName].filter(Boolean).join(' ').trim();
  const vascoNumberValid = VASCO_NUMBER_RE.test(vascoNumberInput.trim());
  const creditNotesNet = creditNotes.reduce((acc, cn) => acc + (parseFloat(cn.amount) || 0), 0);
  // A link always wins: an order that got a payment after its force close shows its real coverage.
  const isForceClosed = !!order.forceClosedAt && payments.length === 0;
  const canForceClose = !order.forceClosedAt && payments.length === 0;

  return (
    <div className="p-6 pb-20">
      <PageHeader
        title={`Order #${order.orderNumber}`}
        subtitle={customer}
        onBack={() => navigate('/accounting/b2b-orders')}
        reserveWidthPx={0}
        actions={<></>}
      />

      {error && (
        <AlertMessage type="error" message={error} title="" onDismiss={() => setError('')} dismissible>
          {''}
        </AlertMessage>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column — order, payments, Vasco documents */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order metadata */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{formatDateTime(order.orderDate)}</h3>
                  <a
                    href={getWooCommerceOrderUrl(order.wooOrderId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    Open in WooCommerce
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-700 tabular-nums">
                    {order.currency} {orderTotal.toFixed(2)}
                  </div>
                  {isForceClosed ? (
                    <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-teal-700 bg-teal-50">
                      <Lock className="h-3 w-3" />
                      Force closed
                    </span>
                  ) : (
                    <span
                      className={`mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${COVERAGE_CLASSES[coverage.status]}`}
                    >
                      {COVERAGE_LABELS[coverage.status]}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Customer</span>
                <p className="text-sm text-gray-800 mt-0.5">{customer}</p>
                {order.billingCompany && personalName && (
                  <p className="text-xs text-gray-500">{personalName}</p>
                )}
                {order.customerEmail && <p className="text-xs text-gray-500">{order.customerEmail}</p>}
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Payment method</span>
                <p className="text-sm text-gray-800 mt-0.5">{order.paymentMethod || '—'}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Order status</span>
                <p className="text-sm text-gray-800 mt-0.5">{order.status || '—'}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Invoice number</span>
                <p className="text-sm text-gray-800 mt-0.5">{order.invoiceNumber || '—'}</p>
              </div>
            </div>
          </div>

          {/* Coverage summary */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Payment coverage</h3>
            </div>
            <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Order total</span>
                <p className="text-lg font-semibold text-gray-900 tabular-nums mt-0.5">
                  {order.currency} {coverage.total.toFixed(2)}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Linked payments ({payments.length})
                </span>
                <p className="text-lg font-semibold text-gray-900 tabular-nums mt-0.5">
                  {order.currency} {coverage.covered.toFixed(2)}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {coverage.status === 'overpaid' ? 'Overpaid by' : 'Remaining'}
                </span>
                <p
                  className={`text-lg font-semibold tabular-nums mt-0.5 ${
                    Math.abs(coverage.remaining) <= 0.01
                      ? 'text-green-700'
                      : coverage.remaining > 0
                      ? 'text-yellow-700'
                      : 'text-orange-700'
                  }`}
                  data-testid="coverage-remaining"
                >
                  {order.currency} {(coverage.status === 'overpaid' ? -coverage.remaining : coverage.remaining).toFixed(2)}
                </p>
              </div>
            </div>
            {isForceClosed && (
              <div className="px-6 pb-5 -mt-2 space-y-2" data-testid="order-force-closed">
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  <p className="whitespace-pre-wrap">{order.forceCloseNote}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Force closed by {order.forceClosedByName || 'the order sync'}
                    {order.forceClosedAt ? ` · ${formatDateTime(order.forceClosedAt)}` : ''}
                  </p>
                </div>
                <Button variant="secondary" size="sm" icon={RotateCcw} onClick={() => setShowReopenModal(true)}>
                  Reopen
                </Button>
              </div>
            )}
            {canForceClose && (
              <div className="px-6 pb-5 -mt-2">
                <Button variant="secondary" size="sm" icon={Lock} onClick={() => setShowForceCloseModal(true)}>
                  Force close
                </Button>
                <p className="mt-1 text-xs text-gray-500">
                  Close this order if it will never be linked to a payment. A note is required.
                </p>
              </div>
            )}
            {creditNotes.length > 0 && (
              <div className="px-6 pb-5 -mt-2 text-sm text-gray-600">
                Vasco credit notes on this order:{' '}
                <span className="tabular-nums">
                  {creditNotes.map((cn) => cn.number).join(', ')} (EUR {creditNotesNet.toFixed(2)})
                </span>
              </div>
            )}
          </div>

          {/* Linked payments */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Linked Payments</h3>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label htmlFor="payment-search" className="block text-sm font-medium text-gray-700 mb-2">
                  Add a payment
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      id="payment-search"
                      type="text"
                      value={paymentSearch}
                      onChange={(e) => setPaymentSearch(e.target.value)}
                      placeholder="Search by reference, counterparty, remittance info or amount…"
                      className="w-full h-10 pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent"
                    />
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={amountInput}
                    onChange={(e) => setAmountInput(e.target.value)}
                    placeholder={`Amount (${order.currency})`}
                    title="How much of this order the payment covers. Leave blank for the full order total."
                    className="w-44 h-10 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Leave the amount blank to claim the full order total.
                </p>

                {searching && <p className="mt-2 text-xs text-gray-400">Searching payments…</p>}

                {!searching && paymentSearch.trim().length >= 2 && candidates.length === 0 && (
                  <p className="mt-2 text-sm text-gray-500">No matching payments.</p>
                )}

                {candidates.length > 0 && (
                  <div className="mt-2 space-y-2" data-testid="payment-candidates">
                    {candidates.map((candidate) => (
                      <div
                        key={candidate.id}
                        className="flex items-center justify-between py-2 px-3 border border-gray-200 rounded-md bg-gray-50"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-gray-900">
                              {candidate.counterparty || 'Unknown counterparty'}
                            </span>
                            <span className="text-xs text-gray-500">{formatDateTime(candidate.date)}</span>
                            {candidate.paymentSource && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                                {candidate.paymentSource}
                              </span>
                            )}
                            {candidate.linkedOrderCount > 0 && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                                {candidate.linkedOrderCount} order(s) linked
                              </span>
                            )}
                          </div>
                          {candidate.remittanceInfo && (
                            <p className="text-xs text-gray-500 truncate max-w-xl">{candidate.remittanceInfo}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                          <span className="text-sm font-medium text-gray-900 tabular-nums">
                            {candidate.currency} {parseFloat(candidate.amount).toFixed(2)}
                          </span>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleLinkPayment(candidate.id)}
                            disabled={linkingPaymentId === candidate.id}
                            icon={Plus}
                          >
                            {linkingPaymentId === candidate.id ? 'Adding…' : 'Add'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {payments.length > 0 ? (
                <div className="space-y-2">
                  {payments.map((payment) => {
                    const claimed = parseFloat(payment.claimedAmount ?? order.total) || 0;
                    const isPartial =
                      payment.partialAmount != null
                      && Math.abs(parseFloat(payment.partialAmount) - orderTotal) > 0.005;
                    return (
                      <div
                        key={payment.id}
                        data-testid={`linked-payment-${payment.id}`}
                        className="flex items-center justify-between py-2 px-3 border border-gray-200 rounded-md"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-wrap">
                          <Link2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <button
                            type="button"
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                            onClick={() => navigate(`/accounting/payments/${payment.id}`)}
                          >
                            {payment.counterparty || `Payment #${payment.id}`}
                          </button>
                          <span className="text-xs text-gray-500">{formatDateTime(payment.date)}</span>
                          {payment.paymentSource && (
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                              {payment.paymentSource}
                            </span>
                          )}
                          {isPartial && (
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                              Partial
                            </span>
                          )}
                          {payment.forceClosedAt && (
                            <span
                              className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-200 text-gray-700"
                              title={payment.forceCloseNote || ''}
                            >
                              Force closed
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                          <div className="text-right tabular-nums">
                            <div className="text-sm font-medium text-gray-900">
                              {payment.currency} {claimed.toFixed(2)}
                            </div>
                            {isPartial && (
                              <div className="text-xs text-gray-400">
                                of {payment.currency} {parseFloat(payment.amount).toFixed(2)} paid
                              </div>
                            )}
                          </div>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleRemovePayment(payment.id)}
                            disabled={removingPaymentId === payment.id}
                            icon={X}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  <div className="flex justify-end pt-1 border-t border-gray-100">
                    <div className="text-right">
                      <span className="text-sm text-gray-500 mr-2">Total linked:</span>
                      <span className="text-sm font-semibold tabular-nums text-gray-900">
                        {order.currency} {coverage.covered.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No payments linked yet.</p>
              )}
            </div>
          </div>

          {/* Vasco documents */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Vasco Invoices</h3>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label htmlFor="order-vasco-number" className="block text-sm font-medium text-gray-700 mb-2">
                  Link a Vasco invoice
                </label>
                <div className="flex gap-2">
                  <input
                    id="order-vasco-number"
                    type="text"
                    value={vascoNumberInput}
                    onChange={(e) => {
                      setVascoNumberInput(e.target.value);
                      if (vascoError) setVascoError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && vascoNumberValid && !vascoLinking) handleLinkVasco();
                    }}
                    onBlur={() => {
                      const trimmed = vascoNumberInput.trim();
                      if (trimmed && !trimmed.includes('/')) {
                        setVascoNumberInput(`${trimmed}/${new Date().getFullYear()}`);
                      }
                    }}
                    placeholder={`e.g. 3814/${new Date().getFullYear()}`}
                    disabled={vascoLinking}
                    className="flex-1 h-10 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent disabled:bg-gray-50"
                  />
                  <Button
                    variant="primary"
                    onClick={handleLinkVasco}
                    disabled={!vascoNumberValid || vascoLinking}
                    icon={Plus}
                  >
                    {vascoLinking ? 'Adding…' : 'Add'}
                  </Button>
                </div>

                {vascoError && (
                  <div className="mt-2 flex items-start text-sm text-red-600">
                    <AlertTriangle className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                    <span>{vascoError}</span>
                  </div>
                )}

                <p className="mt-1 text-xs text-gray-500">
                  A number the Vasco import does not know yet is linked as Unverified and is verified
                  automatically on the next import.
                </p>
              </div>

              {vascoDocuments.length > 0 ? (
                <div className="space-y-2">
                  {vascoDocuments.map((doc: OrderVascoDocument) => (
                    <VascoDocumentRow
                      key={doc.linkId != null ? `manual-${doc.linkId}` : `import-${doc.invoiceId}`}
                      testId={`vasco-doc-${doc.invoiceNumber}`}
                      verified={doc.verified}
                      invoiceNumber={doc.invoiceNumber}
                      buyer={doc.buyer}
                      chips={
                        <>
                          {doc.source === 'import' ? (
                            <VascoChip>From Vasco import</VascoChip>
                          ) : (
                            <VascoChip tone="blue">Linked by hand</VascoChip>
                          )}
                          {!doc.verified && <VascoChip tone="amber">Unverified</VascoChip>}
                          {doc.isCreditNote && <VascoChip tone="purple">Credit note</VascoChip>}
                          {doc.source === 'manual'
                            && doc.invoiceOrderNumber
                            && doc.invoiceOrderNumber !== order.orderNumber && (
                            <VascoChip tone="amber" title="The Vasco document names a different order">
                              Vasco says order #{doc.invoiceOrderNumber}
                            </VascoChip>
                          )}
                        </>
                      }
                      amount={
                        doc.amount != null ? (
                          <span className="text-sm font-medium text-gray-900 tabular-nums">
                            EUR {parseFloat(doc.amount).toFixed(2)}
                          </span>
                        ) : null
                      }
                      action={
                        doc.source === 'manual' && doc.linkId != null ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleRemoveVascoLink(doc.linkId as number)}
                            disabled={removingVascoLinkId === doc.linkId}
                            icon={X}
                          >
                            Remove
                          </Button>
                        ) : null
                      }
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No Vasco invoices linked yet.</p>
              )}
            </div>
          </div>
        </div>

        {/* Right column — internal notes (sticky sidebar) */}
        <div className="lg:col-span-1 space-y-6 lg:sticky lg:top-8 lg:max-h-[calc(100vh-4rem)] lg:overflow-y-auto pb-8 custom-scrollbar">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="px-6 py-5">
              <InternalNotesThread
                ref={notesRef}
                entityId={order.id}
                apiBase={`/api/accounting/b2b-orders/${order.id}`}
                emptyHint="Add internal notes to track this order and communicate with your team."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Force close modal. The note is required. */}
      <Modal
        isOpen={showForceCloseModal}
        onClose={() => { if (!forceClosing) { setShowForceCloseModal(false); setForceCloseNote(''); } }}
        title="Force close order"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            The order leaves the &quot;Payment not linked&quot; list. Explain why it will never be linked
            to a payment. The note is also added to the internal notes.
          </p>
          <div>
            <label htmlFor="order-force-close-note" className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Note (required)
            </label>
            <Textarea
              id="order-force-close-note"
              value={forceCloseNote}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForceCloseNote(e.target.value)}
              placeholder="e.g. Paid in cash at a fair, no bank record."
              rows={4}
              disabled={forceClosing}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => { setShowForceCloseModal(false); setForceCloseNote(''); }}
              disabled={forceClosing}
              icon={undefined}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleForceClose}
              loading={forceClosing}
              disabled={!forceCloseNote.trim() || forceClosing}
              icon={Lock}
            >
              Force close
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={showReopenModal}
        onClose={() => setShowReopenModal(false)}
        onConfirm={handleReopen}
        title="Reopen order"
        message='The order goes back to the "Payment not linked" list. The automatic rules will not close it again.'
        confirmText="Reopen"
        type="primary"
      />
    </div>
  );
};

export default B2BOrderDetail;
